import {useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {useQuery} from "@tanstack/react-query";
import {getUserPaymentCard} from "api";
import {cardTypeLogos} from "conts";
import {bookingErrorMessage, buildBookingPayload, useBookingRequest} from "pages/user/PaymentPage/hooks/useBookingRequest";
import {Alert, AlertIcon, PillButton, Spinner, tokens} from "ui/blocks/guestMessaging/GuestUI";
import {Agreement, CardField} from "./CheckoutParts";

const NEW_CARD = "new";

/**
 * Checkout for a signed-in student: a saved card (default first) or a new
 * card, then the same booking request as the payment page.
 */
export const AccountCheckout = ({bookingInput, cancellation, instructorName, disabled, initialError, onBooked}) => {
    const {createCardPaymentMethod, requestBooking, isSubmitting, stripeReady} = useBookingRequest();
    const [selected, setSelected] = useState(null);
    const [card, setCard] = useState({complete: false, error: ""});
    const [error, setError] = useState(initialError || "");

    useEffect(() => {
        if (initialError) setError(initialError);
    }, [initialError]);

    const {data: cardsResp, isLoading: cardsLoading} = useQuery({
        queryKey: ["getUserPaymentCard"],
        queryFn: getUserPaymentCard,
        refetchOnWindowFocus: false,
    });
    const savedCards = useMemo(() => cardsResp?.data ?? [], [cardsResp]);

    // Default card first; otherwise a new card.
    useEffect(() => {
        if (cardsLoading) return;
        if (selected && (selected === NEW_CARD || savedCards.some((c) => c.stripe_card_id === selected))) return;
        const preferred = savedCards.find((c) => c.is_default) || savedCards[0];
        setSelected(preferred ? preferred.stripe_card_id : NEW_CARD);
    }, [savedCards, cardsLoading, selected]);

    const submit = async (e) => {
        e.preventDefault();
        if (isSubmitting || disabled || !selected) return;
        setError("");
        const isNewCard = selected === NEW_CARD;
        if (isNewCard && !card.complete) {
            setCard((c) => ({...c, error: c.error || "Enter your card details."}));
            return;
        }
        try {
            const cardId = isNewCard ? await createCardPaymentMethod() : selected;
            const booking = await requestBooking({payload: buildBookingPayload(bookingInput), cardId, isNewCard});
            if (booking) onBooked(booking);
        } catch (err) {
            setError(bookingErrorMessage(err));
        }
    };

    return (
        <Section as="form" onSubmit={submit} noValidate aria-busy={isSubmitting}>
            <SectionTitle>Payment</SectionTitle>
            {cardsLoading ? (
                <Spinner $size={18}/>
            ) : (
                <Cards role="radiogroup" aria-label="Payment method">
                    {savedCards.map((c) => (
                        <CardOption
                            key={c.stripe_card_id}
                            type="button"
                            role="radio"
                            aria-checked={selected === c.stripe_card_id}
                            $selected={selected === c.stripe_card_id}
                            onClick={() => setSelected(c.stripe_card_id)}
                            disabled={isSubmitting}
                        >
                            {cardTypeLogos?.[c.card_type]?.img ?? <span aria-hidden="true">💳</span>}
                            <span>•••• {c.card_last_4_digit}</span>
                            {c.expiry_date ? <Exp>Exp. {c.expiry_date}</Exp> : null}
                        </CardOption>
                    ))}
                    <CardOption
                        type="button"
                        role="radio"
                        aria-checked={selected === NEW_CARD}
                        $selected={selected === NEW_CARD}
                        onClick={() => setSelected(NEW_CARD)}
                        disabled={isSubmitting}
                    >
                        <span aria-hidden="true">＋</span>
                        <span>{savedCards.length ? "Use a new card" : "New card"}</span>
                    </CardOption>
                </Cards>
            )}

            {selected === NEW_CARD && (
                <CardField error={card.error} disabled={isSubmitting} onChange={(c) => setCard(c)}/>
            )}

            {error && <Alert role="alert"><AlertIcon/><span>{error}</span></Alert>}

            <Agreement cancellation={cancellation} instructorName={instructorName}/>

            <PillButton type="submit" $block disabled={isSubmitting || disabled || !stripeReady || !selected}>
                {isSubmitting ? <><Spinner $light $size={16}/> Sending request…</> : "Request to book"}
            </PillButton>
        </Section>
    );
};

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-top: 4px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: ${tokens.ink};
`;

const Cards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CardOption = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    box-sizing: border-box;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid ${({$selected}) => ($selected ? tokens.accent : tokens.border)};
    background: ${({$selected}) => ($selected ? tokens.accentSoft : "#fff")};
    font: inherit;
    font-size: 15px;
    color: ${tokens.text};
    text-align: left;
    cursor: pointer;

    img, svg {
        height: 20px;
        width: auto;
    }
`;

const Exp = styled.span`
    margin-left: auto;
    font-size: 13px;
    color: ${tokens.muted};
`;
